import { ProductFeature, ProductFeatureStatus } from "components/product/product.types";
import { PlanProps } from "shared/components/plan/plan";
import { LIBUM_DOCS_NEXUM, SALES_MAILTO } from "shared/constants/globals";
import { PlanOfferings } from "shared/constants/plans";
import { createItems } from "shared/constants/utilities";

export const NEXUM_API_FEATURES: ProductFeature[] = [{
  id: 'naas',
  name: 'NexumAPI as a Service (NaaS)',
  description: 'Use NexumAPI as a ready-to-use service called "NaaS" for language agnostic implementation',
  isFeatured: true,
  isHidden: false,
  imagePath: '/images/9acdaedd-4b1f-4d66-b72f-7f03949268a5.png',
  status: ProductFeatureStatus.AVAILABLE,
}, {
  id: 'comprehensive-docs',
  name: 'Comprehensive Docs',
  description: 'Build out your NexumAPI requests with our beautiful documentation & API reference',
  isFeatured: true,
  isHidden: false,
  imagePath: '/images/11955004-5972-430f-86b6-40470d8f17ec.png',
  status: ProductFeatureStatus.PARTIALLY_AVAILABLE,
  docsLink: `${LIBUM_DOCS_NEXUM}/capabilities#full-content-symx-api-reference`,
}, {
  id: 'http-error-codes',
  name: 'HTTP Error Codes',
  description: 'We translated all SymXchange error responses to HTTP error codes for better stack management',
  isFeatured: false,
  isHidden: false,
  imagePath: '',
  status: ProductFeatureStatus.AVAILABLE,
  docsLink: `${LIBUM_DOCS_NEXUM}/capabilities#http-error-codes`,
}, {
  id: 'order-agnostic-requests',
  name: 'Order Agnostic Requests',
  description: 'Let us worry about the order of the elements in your SymXchange requests',
  isFeatured: false,
  isHidden: false,
  imagePath: '',
  status: ProductFeatureStatus.AVAILABLE,
  docsLink: `${LIBUM_DOCS_NEXUM}/capabilities#recursive-order-agnostic-request-elements`,
}, {
  id: 'form-url-encoded',
  name: 'Form URL-Encoded',
  description: 'Easily send silent NaaS requests through custom PowerOn to bypass FM validation restrictions',
  isFeatured: false,
  isHidden: false,
  imagePath: '',
  status: ProductFeatureStatus.AVAILABLE,
  docsLink: `${LIBUM_DOCS_NEXUM}/capabilities#form-urlencoded-content-type`,
}, {
  id: 'multi-version-support',
  name: 'Multi-Version Support',
  description: 'We maintain support for updating service versions with a 1-to-1 mapping to all SymX SOAP services and operations',
  isFeatured: false,
  isHidden: true,
  imagePath: '',
  status: ProductFeatureStatus.AVAILABLE,
  docsLink: `${LIBUM_DOCS_NEXUM}/capabilities#multi-version-support`,
}, {
  id: 'symx-passthrough',
  name: 'SymXchange Passthrough Mode',
  description: 'Gain insight on all third-party connections to your SymXchange instance. Route all of your vendor\'s native SymXchange connections through us and we will record all of the activity for you',
  isFeatured: false,
  isHidden: true,
  imagePath: '',
  status: ProductFeatureStatus.AVAILABLE,
  docsLink: `${LIBUM_DOCS_NEXUM}/capabilities#symxchange-native-passthrough`,
}, {
  id: 'improved-logging',
  name: 'Improved Logging',
  description: 'Track all SymX requests and bubble up critical user information with zero configuration',
  isFeatured: false,
  isHidden: true,
  imagePath: '',
  status: ProductFeatureStatus.AVAILABLE,
  docsLink: `${LIBUM_DOCS_NEXUM}/capabilities#detailed-logging-transports`,
}, {
  id: 'vpn-tunneling',
  name: 'VPN Tunneling',
  description: 'Opt-in to a VPN tunnel to securely connect to Libum-hosted NaaS',
  isFeatured: false,
  isHidden: true,
  imagePath: '',
  status: ProductFeatureStatus.AVAILABLE,
}, {
  id: 'managed-infra',
  name: 'Managed infrastructure',
  description: 'We manage all of the infrastructure so that you don\'t have to',
  isFeatured: false,
  isHidden: true,
  imagePath: '',
  status: ProductFeatureStatus.AVAILABLE,
  docsLink: `${LIBUM_DOCS_NEXUM}/capabilities#managed-infrastructure`,
}, {
  id: 'automatic-updates',
  name: 'Authomatic updates',
  description: 'We automatically update NexumAPI on new releases without you needing to lift a finger',
  isFeatured: false,
  isHidden: true,
  imagePath: '',
  status: ProductFeatureStatus.AVAILABLE,
  docsLink: `${LIBUM_DOCS_NEXUM}/capabilities#automatic-updates`,
}, {
  id: 'load-balancing-auto-scaling',
  name: 'Load balancing & auto scaling',
  description: 'We will load balance requests and automatically provision new compute resources to meet any level of SymXchange demand',
  isFeatured: false,
  isHidden: true,
  imagePath: '',
  status: ProductFeatureStatus.AVAILABLE,
  docsLink: `${LIBUM_DOCS_NEXUM}/capabilities#load-balancing-and-auto-scaling`,
}, {
  id: 'high-availability',
  name: 'High availability',
  description: 'We host your NexumAPI instance in multiple availability zones to ensure that we are always operational, even if any zone goes down',
  isFeatured: false,
  isHidden: true,
  imagePath: '',
  status: ProductFeatureStatus.AVAILABLE,
  docsLink: `${LIBUM_DOCS_NEXUM}/capabilities#high-availability`,
}, {
  id: 'chained-requests',
  name: 'Chained Requests',
  description: 'We built custom routes which combine SymX requests to reduce the development burden',
  isFeatured: false,
  isHidden: true,
  imagePath: '',
  status: ProductFeatureStatus.PLANNED,
  docsLink: `${LIBUM_DOCS_NEXUM}/capabilities#custom-chained-actions`,
}];

export const NEXUM_API_PLAN_FEATURES = [{
  name: 'Support for all SymXchange SOAP services',
  offerings: [{
    name: PlanOfferings.NEXUM_SELF_HOSTED,
  }, {
    name: PlanOfferings.NEXUM_LIBUM_HOSTED,
  }],
}, {
  name: 'Maintain 1-to-1 SymXchange version parity',
  offerings: [{
    name: PlanOfferings.NEXUM_SELF_HOSTED,
  }, {
    name: PlanOfferings.NEXUM_LIBUM_HOSTED,
  }],
}, {
  name: 'Comprehensive Docs & SymX API reference',
  offerings: [{
    name: PlanOfferings.NEXUM_SELF_HOSTED,
  }, {
    name: PlanOfferings.NEXUM_LIBUM_HOSTED,
  }],
}, {
  name: 'Language agnostic, ready-to-use service',
  offerings: [{
    name: PlanOfferings.NEXUM_SELF_HOSTED,
  }, {
    name: PlanOfferings.NEXUM_LIBUM_HOSTED,
  }],
}, {
  name: 'Custom logging/monitoring of SymXchange usage',
  offerings: [{
    name: PlanOfferings.NEXUM_SELF_HOSTED,
  }, {
    name: PlanOfferings.NEXUM_LIBUM_HOSTED,
  }],
}, {
  name: 'SymXchange Passthrough Mode',
  offerings: [{
    name: PlanOfferings.NEXUM_SELF_HOSTED,
  }, {
    name: PlanOfferings.NEXUM_LIBUM_HOSTED,
  }],
}, {
  name: 'Multi-operation chained actions',
  offerings: [{
    name: PlanOfferings.NEXUM_SELF_HOSTED,
  }, {
    name: PlanOfferings.NEXUM_LIBUM_HOSTED,
  }],
}, {
  name: 'Optional VPN tunnel',
  offerings: [{
    name: PlanOfferings.NEXUM_LIBUM_HOSTED,
  }],
}, {
  name: 'Managed infrastructure',
  offerings: [{
    name: PlanOfferings.NEXUM_LIBUM_HOSTED,
  }],
}, {
  name: 'Automatic updates',
  offerings: [{
    name: PlanOfferings.NEXUM_LIBUM_HOSTED,
  }],
}, {
  name: 'Load balancing & auto scaling',
  offerings: [{
    name: PlanOfferings.NEXUM_LIBUM_HOSTED,
  }],
}, {
  name: 'High availability',
  offerings: [{
    name: PlanOfferings.NEXUM_LIBUM_HOSTED,
  }],
}];
export const NEXUM_API_PLAN_FEATURES_MAP = createItems(NEXUM_API_PLAN_FEATURES, 'nexum');

export const NEXUM_API_PLAN: PlanProps = {
  header: 'Pick the NexumAPI offering that works for you',
  features: NEXUM_API_PLAN_FEATURES_MAP,
  offerings: [{
    name: PlanOfferings.NEXUM_SELF_HOSTED,
    icon: '/logos/naas_self',
    id: 'offering-nexum-self-hosted',
    class: 'nexum',
    link: {
      href: SALES_MAILTO,
      text: 'Contact Sales',
    },
  },
  {
    name: PlanOfferings.NEXUM_LIBUM_HOSTED,
    icon: '/logos/naas_libum',
    id: 'offering-nexum-libum-hosted',
    class: 'nexum',
    link: {
      href: SALES_MAILTO,
      text: 'Contact Sales',
    },
  }]
}
